import React, { useState } from "react";
import BackButton from "./BackButton";
import Loading from "../../Util/Loading";
import ProcessingBar2 from "../ProcessingBar/ProcessingBar2";

const STATUS_OPTIONS = [
  "pending for approval",
  "preparing",
  "ready",
  "completed",
  "cancelled",
];

const STATUS_STYLES = {
  "pending for approval": "bg-orange-100 text-orange-600",
  preparing: "bg-yellowlight text-yellowdark",
  ready: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
};

const Header = ({ order, onUpdateStatus, onRefund }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(null);

  const [refundLoading, setRefundLoading] = useState(false);

  const handleChangeStatus = async (status) => {
    if (status === order.orderStatus) return;

    try {
      setLoading(true);
      setLoadingStatus(status); // 🔥 track which button

      await onUpdateStatus(order._id, status);
      setIsOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingStatus(null);
    }
  };

  const handleRefund = async () => {
    try {
      setRefundLoading(true);
      await onRefund(order._id);
    } catch (err) {
      console.error(err);
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <>
      <BackButton label="Order Detail" to="/ordering" />

      <div className="bg-white rounded-xl p-4 shadow space-y-3">
        {/* HEADER */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-lg font-semibold">#{order._id.slice(-4)}</h1>

            <p className="text-gray-500 text-sm">
              {new Date(order.createdAt).toLocaleString()}
            </p>

            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 mt-2 rounded-full text-xs font-semibold tracking-wide shadow-sm ${
                STATUS_STYLES[order.orderStatus] || "bg-gray-100 text-gray-600"
              }`}
            >
              {/* 🔥 status dot */}
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>

              {/* label */}
              <span className="capitalize">{order.orderStatus}</span>
            </span>
          </div>

          {/* STATUS BADGE (CLICKABLE) */}
          <div className="flex items-center gap-2">
            {/* STATUS BADGE */}
            {order.orderStatus === "cancelled" &&
              order.payments?.paymentStatus === "paid" && (
                <button
                  onClick={handleRefund}
                  disabled={refundLoading}
                  className="text-xs px-3 py-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-50"
                >
                  {refundLoading ? "Processing..." : "Refund"}
                </button>
              )}

            {order.payments?.paymentStatus === "refunded" && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-red-50 to-rose-50 border border-red-100 shadow-sm">
                {/* Dot */}
                <div className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>

                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </div>

                {/* Text */}
                <span className="text-xs font-semibold text-red-600 tracking-wide">
                  Refunded
                </span>
              </div>
            )}

            {/* 🔥 CHANGE BUTTON */}
            <button
              onClick={() => setIsOpen(true)}
              className="text-xs px-3 py-1 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-100 transition"
            >
              Update Status
            </button>
          </div>
        </div>
      </div>

      {/* 🔥 MODAL */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          {/* BACKDROP */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsOpen(false)}
          />

          {/* CONTENT */}
          <div className="relative bg-white w-full md:w-[400px] rounded-t-2xl md:rounded-2xl p-5 animate-slideUp">
            <h2 className="text-lg font-semibold mb-4 text-navy">
              Update Order Status
            </h2>

            <div className="flex flex-col gap-2">
              {STATUS_OPTIONS.map((status) => {
                const isLoading = loadingStatus === status;

                return (
                  <button
                    key={status}
                    onClick={() => handleChangeStatus(status)}
                    disabled={loadingStatus !== null} // 🔥 disable all when loading
                    className={`px-4 py-2 rounded-lg text-sm text-left flex items-center justify-between transition ${
                      status === order.orderStatus
                        ? "bg-black text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    } ${loadingStatus && !isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {/* LEFT: label */}
                    <span>{status}</span>

                    {/* RIGHT: loading */}
                    {isLoading && <ProcessingBar2 />}
                  </button>
                );
              })}
            </div>

            {/* CANCEL */}
            <button
              onClick={() => setIsOpen(false)}
              className="mt-4 w-full text-sm text-gray-500 hover:text-black"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
